/**
 * 記事プレビューページ (Phase 3).
 *
 * bodyHtmlCache をレンダリングして記事の公開イメージを確認する。
 * Figma: #37 (記事プレビュー), #109 (ローディング)
 */

import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { fetchPost, fetchSite } from '../lib/api-client.js';
import { formatErrorMessage } from '../lib/error-messages.js';

export function PostPreviewPage() {
  const { siteId, postId } = useParams({ strict: false }) as {
    siteId: string;
    postId: string;
  };

  const {
    data: post,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['sites', siteId, 'post', postId],
    queryFn: () => fetchPost(siteId, postId),
    enabled: !!siteId && !!postId,
  });

  const siteQuery = useQuery({
    queryKey: ['sites', siteId],
    queryFn: () => fetchSite(siteId),
    enabled: !!siteId,
  });
  // originHost はサーバ経由だが UI 層でもホスト名形式（英数 + . -）を検証
  const rawHost = siteQuery.data?.originHost ?? '';
  const originHost = /^[a-z0-9]([a-z0-9.-]*[a-z0-9])?$/i.test(rawHost) ? rawHost : 'example.com';

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 py-8">
        <div className="h-10 w-2/3 animate-pulse rounded-[6px] bg-[#edebe8]" />
        <div className="h-4 w-1/3 animate-pulse rounded-[6px] bg-[#edebe8]" />
        <div className="mt-8 space-y-3">
          <div className="h-4 w-full animate-pulse rounded-[6px] bg-[#edebe8]" />
          <div className="h-4 w-5/6 animate-pulse rounded-[6px] bg-[#edebe8]" />
          <div className="h-4 w-4/6 animate-pulse rounded-[6px] bg-[#edebe8]" />
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="mx-auto max-w-3xl py-8">
        <div className="rounded-[6px] border-l-4 border-[#ef4444] bg-[#fef2f2] p-4 text-sm text-[#991b1b]">
          {error
            ? formatErrorMessage(error, '記事の読み込みに失敗しました')
            : '記事が見つかりません'}
        </div>
        <Link
          to="/sites/$siteId/posts"
          params={{ siteId }}
          className="mt-4 inline-block text-sm text-[#3b82f6] hover:underline"
        >
          ← 記事一覧に戻る
        </Link>
      </div>
    );
  }

  const statusLabel: Record<string, string> = {
    draft: '下書き',
    scheduled: '予約投稿',
    published: '公開済',
    unlisted: '非公開',
    trash: 'ゴミ箱',
  };

  return (
    <div className="mx-auto max-w-3xl py-8">
      {/* プレビューバー */}
      <div className="mb-6 flex items-center justify-between rounded-[8px] border-l-4 border-[#f59e0b] bg-[#fffbeb] px-4 py-2">
        <div className="flex items-center gap-2 text-[13px] text-[#92400e]">
          <span className="font-medium">プレビュー</span>
          <span className="rounded-full border border-[#f59e0b] bg-[#fffbeb] px-2 py-0.5 text-xs font-medium">
            {statusLabel[post.status] ?? post.status}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {post.status === 'published' && (
            <a
              href={`https://${originHost}/posts/${post.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-[#065f46] hover:underline"
            >
              公開ページを開く ↗
            </a>
          )}
          <Link
            to="/sites/$siteId/posts/$postId/edit"
            params={{ siteId, postId }}
            className="text-sm font-medium text-[#3b82f6] hover:text-[#1d4ed8]"
          >
            編集に戻る
          </Link>
          <Link
            to="/sites/$siteId/posts"
            params={{ siteId }}
            className="text-sm text-[#706d65] hover:text-[#23221e]"
          >
            一覧
          </Link>
        </div>
      </div>

      {/* 記事ヘッダー */}
      <header className="mb-8">
        <h1 className="text-3xl font-bold leading-tight tracking-[0.03em] text-[#23221e]">
          {post.title}
        </h1>
        {post.excerpt && <p className="mt-3 text-lg text-[#706d65]">{post.excerpt}</p>}
        <div className="mt-4 flex items-center gap-4 text-sm text-[#706d65]">
          {post.publishedAt && (
            <time>{new Date(post.publishedAt).toLocaleDateString('ja-JP')}</time>
          )}
          <span>rev {post.rev}</span>
        </div>
      </header>

      {/* 記事本文 */}
      {post.bodyHtmlCache ? (
        <article
          className="prose prose-stone max-w-none"
          // bodyHtmlCache は admin-api で sanitizeContentHtml 済み
          // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized HTML from server
          dangerouslySetInnerHTML={{ __html: post.bodyHtmlCache }}
        />
      ) : (
        <div className="rounded-[8px] border border-[#edebe8] bg-[#f8f7f6] px-6 py-12 text-center text-sm text-[#706d65]">
          <p>本文がまだありません</p>
          <p className="mt-1 text-xs">
            記事エディタで本文を入力して保存すると、ここにプレビューが表示されます
          </p>
        </div>
      )}

      {/* SEO メタ情報プレビュー */}
      {(post.metaTitle || post.metaDescription) && (
        <div className="mt-12 rounded-[8px] border border-[#edebe8] bg-[#f8f7f6] p-4">
          <h2 className="mb-3 text-xs font-medium uppercase tracking-[0.04em] text-[#706d65]">
            SEO プレビュー
          </h2>
          <div className="space-y-1">
            <div className="text-lg font-medium text-[#1e40af]">{post.metaTitle || post.title}</div>
            <div className="text-sm text-[#065f46]">
              https://{originHost}/posts/{post.slug}
            </div>
            {post.metaDescription && (
              <div className="text-sm text-[#706d65]">{post.metaDescription}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
